import React, { useState } from 'react';
import { X, Leaf, Sun, Wind, Battery, CheckCircle, ArrowRight, ArrowLeft, Upload } from 'lucide-react';
import { InstalledRenewable, BatteryStorage, LocationData } from '../types';
import { RENEWABLE_TYPES, SOLAR_PROFILE, WIND_PROFILE, GREEN_CERT_RENEWABLE_FRACTION, GREEN_CERT_REVENUE_BONUS, LOCATION_RENEWABLE_FACTORS } from '../constants';

interface GreenEnergyModalProps {
  onClose: () => void;
  currentLocation: string;
  locations: Record<string, LocationData>;
  money: number;
  currentHour: number;
  onInstallRenewable: (locationId: string, sourceType: 'solar' | 'wind', units: number) => void;
  onInstallBattery: (locationId: string) => void;
  onToggleBatteryTrading: (locationId: string, enabled: boolean) => void;
  renewablePowerKWByLocation: Record<string, number>;
  greenCertifiedLocations: string[];
  formatCurrency: (amount: number, decimals?: number) => string;
}

const GreenEnergyModal: React.FC<GreenEnergyModalProps> = ({
  onClose,
  currentLocation,
  locations,
  money,
  currentHour,
  onInstallRenewable,
  onInstallBattery,
  onToggleBatteryTrading,
  renewablePowerKWByLocation,
  greenCertifiedLocations,
  formatCurrency,
}) => {
  const [units, setUnits] = useState(1);
  const loc = locations[currentLocation];
  const isGreenCert = greenCertifiedLocations.includes(currentLocation);

  if (!loc?.unlocked) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-900 rounded-xl border border-gray-700 p-8 text-center">
          <p className="text-gray-400">Location not unlocked.</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-700 rounded text-white text-sm">Close</button>
        </div>
      </div>
    );
  }

  const renewables: InstalledRenewable[] = loc.renewableInstallations ?? [];
  const battery: BatteryStorage | null = loc.batteryStorage ?? null;

  const totalPowerDrawKW = Object.values(loc.floors ?? {}).reduce((acc, floor) =>
    acc + floor.racks.reduce((ra, rack) =>
      ra + rack.servers.reduce((sa, s) => sa + s.powerDraw, 0), 0), 0);

  // Current-hour renewable output (using actual profiles, not averages)
  const solarUnits = renewables.filter(r => r.sourceType === 'solar').reduce((a, r) => a + r.units, 0);
  const windUnits  = renewables.filter(r => r.sourceType === 'wind').reduce((a, r) => a + r.units, 0);
  const solarType = RENEWABLE_TYPES.find(r => r.id === 'solar')!;
  const windType  = RENEWABLE_TYPES.find(r => r.id === 'wind')!;

  const locFactors = LOCATION_RENEWABLE_FACTORS[currentLocation] ?? { solar: 1, wind: 1 };
  const currentSolarKW = solarUnits * solarType.capacityKW * SOLAR_PROFILE[currentHour] * locFactors.solar;
  const currentWindKW  = windUnits  * windType.capacityKW  * WIND_PROFILE[currentHour]  * locFactors.wind;
  const currentRenewableKW = currentSolarKW + currentWindKW;

  // Average kW (24h) for coverage % and cert check
  const avgRenewableKW = renewablePowerKWByLocation[currentLocation] ?? 0;
  const renewableFraction = totalPowerDrawKW > 0 ? Math.min(1, avgRenewableKW / totalPowerDrawKW) : 0;

  // Live power flow values this hour
  const surplus = Math.max(0, currentRenewableKW - totalPowerDrawKW);
  const deficit  = Math.max(0, totalPowerDrawKW - currentRenewableKW);

  let batteryAction: 'charging' | 'discharging' | 'full' | 'idle' = 'idle';
  let batteryFlowKW = 0;
  let exportKW = 0;
  let gridDrawKW = totalPowerDrawKW;

  if (battery?.installed) {
    const chargeRateKW = battery.chargeRateKW ?? 500;
    if (surplus > 0) {
      const canCharge = Math.min(surplus, chargeRateKW, battery.capacityKWh - battery.chargeLevel);
      batteryFlowKW = canCharge;
      if (canCharge > 0.1) {
        batteryAction = 'charging';
      } else {
        batteryAction = 'full'; // battery is full, surplus goes straight to export
      }
      exportKW = Math.max(0, surplus - canCharge);
      gridDrawKW = 0;
    } else if (deficit > 0 && battery.chargeLevel > 0) {
      const canDischarge = Math.min(deficit, chargeRateKW, battery.chargeLevel);
      batteryFlowKW = canDischarge;
      batteryAction = 'discharging';
      gridDrawKW = Math.max(0, deficit - canDischarge);
    } else {
      gridDrawKW = deficit;
    }
  } else {
    // No battery: direct renewable offset, remainder from grid
    exportKW = surplus;
    gridDrawKW = deficit;
  }

  const chargePercent = battery ? (battery.chargeLevel / battery.capacityKWh) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5 text-green-400" />
            <div>
              <h2 className="text-lg font-bold text-white">Green Energy — {loc.name}</h2>
              {LOCATION_RENEWABLE_FACTORS[currentLocation] && (
                <p className="text-xs text-gray-400 mt-0.5">{LOCATION_RENEWABLE_FACTORS[currentLocation].label}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Status overview */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-400">Now generating</div>
              <div className="text-xl font-bold text-green-400">{Math.round(currentRenewableKW)} kW</div>
              <div className="text-xs text-gray-500">hour {currentHour}:00</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-400">Avg coverage</div>
              <div className="text-xl font-bold text-emerald-400">{(renewableFraction * 100).toFixed(0)}%</div>
              <div className="text-xs text-gray-500">24h average</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <div className="text-xs text-gray-400">Green cert</div>
              {isGreenCert
                ? <div className="text-sm font-bold text-green-400 flex items-center justify-center gap-1 mt-1"><CheckCircle size={14} /> Certified</div>
                : <div className="text-sm font-bold text-gray-500 mt-1">Not yet</div>
              }
            </div>
          </div>

          {/* Live power flow */}
          {(solarUnits > 0 || windUnits > 0) && (
            <div className="bg-gray-800 rounded-lg p-4">
              <h3 className="font-semibold text-white mb-3 text-sm">Live Power Flow — hour {currentHour}:00</h3>
              <div className="flex items-center gap-2 text-sm flex-wrap">
                {/* Sources */}
                <div className="flex flex-col gap-1">
                  {currentSolarKW > 0 && (
                    <div className="flex items-center gap-1.5 bg-yellow-900/30 border border-yellow-700/40 rounded px-2 py-1">
                      <Sun size={12} className="text-yellow-400 shrink-0" />
                      <span className="text-yellow-300 text-xs">{Math.round(currentSolarKW)} kW solar</span>
                    </div>
                  )}
                  {currentSolarKW === 0 && solarUnits > 0 && (
                    <div className="flex items-center gap-1.5 bg-gray-700/40 rounded px-2 py-1">
                      <Sun size={12} className="text-gray-600 shrink-0" />
                      <span className="text-gray-500 text-xs">Solar — night</span>
                    </div>
                  )}
                  {currentWindKW > 0 && (
                    <div className="flex items-center gap-1.5 bg-blue-900/30 border border-blue-700/40 rounded px-2 py-1">
                      <Wind size={12} className="text-blue-400 shrink-0" />
                      <span className="text-blue-300 text-xs">{Math.round(currentWindKW)} kW wind</span>
                    </div>
                  )}
                </div>

                <ArrowRight size={14} className="text-gray-500 shrink-0" />

                {/* DC load */}
                <div className="bg-gray-700 rounded px-2 py-1 text-xs text-white shrink-0">
                  DC needs {Math.round(totalPowerDrawKW)} kW
                </div>

                {/* Battery */}
                {battery?.installed && batteryAction !== 'idle' && (
                  <>
                    {batteryAction === 'charging' && (
                      <>
                        <ArrowRight size={14} className="text-gray-500 shrink-0" />
                        <div className="flex items-center gap-1 bg-cyan-900/30 border border-cyan-700/40 rounded px-2 py-1">
                          <Battery size={12} className="text-cyan-400" />
                          <span className="text-xs text-cyan-300">charging +{Math.round(batteryFlowKW)} kW</span>
                        </div>
                      </>
                    )}
                    {batteryAction === 'discharging' && (
                      <>
                        <ArrowLeft size={14} className="text-gray-500 shrink-0" />
                        <div className="flex items-center gap-1 bg-cyan-900/30 border border-cyan-700/40 rounded px-2 py-1">
                          <Battery size={12} className="text-cyan-400" />
                          <span className="text-xs text-cyan-300">discharging −{Math.round(batteryFlowKW)} kW</span>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Export */}
                {exportKW > 0 && (
                  <>
                    <ArrowRight size={14} className="text-gray-500 shrink-0" />
                    <div className="flex items-center gap-1 bg-green-900/30 border border-green-700/40 rounded px-2 py-1">
                      <Upload size={12} className="text-green-400" />
                      <span className="text-xs text-green-300">export {Math.round(exportKW)} kW</span>
                    </div>
                  </>
                )}

                {/* Grid draw */}
                {gridDrawKW > 0.5 && (
                  <>
                    <ArrowLeft size={14} className="text-gray-500 shrink-0" />
                    <div className="bg-orange-900/30 border border-orange-700/40 rounded px-2 py-1">
                      <span className="text-xs text-orange-300">grid {Math.round(gridDrawKW)} kW</span>
                    </div>
                  </>
                )}
                {gridDrawKW <= 0.5 && totalPowerDrawKW > 0 && (
                  <div className="text-xs text-emerald-400 font-medium">✓ Running on renewables</div>
                )}
              </div>
            </div>
          )}

          {/* Coverage bar */}
          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>24h average coverage</span>
              <span>Need {(GREEN_CERT_RENEWABLE_FRACTION * 100).toFixed(0)}% for green cert</span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${renewableFraction >= GREEN_CERT_RENEWABLE_FRACTION ? 'bg-green-400' : 'bg-emerald-600'}`}
                style={{ width: `${Math.min(100, renewableFraction * 100)}%` }}
              />
            </div>
            {isGreenCert && (
              <div className="mt-2 text-xs text-green-400">
                ✓ Green certified — +{(GREEN_CERT_REVENUE_BONUS * 100).toFixed(0)}% revenue bonus from green-conscious clients
              </div>
            )}
            {!isGreenCert && renewableFraction > 0 && totalPowerDrawKW > 0 && (
              <div className="mt-2 text-xs text-gray-400">
                {((GREEN_CERT_RENEWABLE_FRACTION - renewableFraction) * totalPowerDrawKW).toFixed(0)} kW more avg renewable needed for certification.
              </div>
            )}
          </div>

          {/* Installed renewables */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-3">Installed Generation</h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-gray-700 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Sun size={14} className="text-yellow-400" />
                  <span className="text-sm text-white">Solar Panels</span>
                </div>
                <div className="text-xl font-bold text-yellow-400">{solarUnits}</div>
                <div className="text-xs text-gray-400">{solarUnits * solarType.capacityKW} kW peak · {formatCurrency(solarUnits * solarType.monthlyMaintenance)}/mo</div>
              </div>
              <div className="bg-gray-700 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Wind size={14} className="text-blue-400" />
                  <span className="text-sm text-white">Wind Turbines</span>
                </div>
                <div className="text-xl font-bold text-blue-400">{windUnits}</div>
                <div className="text-xs text-gray-400">{windUnits * windType.capacityKW} kW peak · {formatCurrency(windUnits * windType.monthlyMaintenance)}/mo</div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <label className="text-xs text-gray-400">Units to install:</label>
              <select
                value={units}
                onChange={e => setUnits(Number(e.target.value))}
                className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white"
              >
                {[1, 2, 5, 10].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {RENEWABLE_TYPES.map(rt => {
                const totalCost = rt.costPerUnit * units;
                const canAfford = money >= totalCost;
                const icon = rt.id === 'solar'
                  ? <Sun size={14} className="text-yellow-400" />
                  : <Wind size={14} className="text-blue-400" />;
                return (
                  <div key={rt.id} className="border border-gray-600 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">{icon}<span className="text-sm font-medium text-white">{rt.name}</span></div>
                    <div className="text-xs text-gray-400 mb-2">{rt.description}</div>
                    <div className="text-xs text-gray-400 mb-1">
                      {rt.capacityKW} kW peak per unit
                      {(() => {
                        const f = rt.id === 'solar' ? locFactors.solar : locFactors.wind;
                        const pct = Math.round((f - 1) * 100);
                        return pct !== 0 ? (
                          <span className={`ml-1 font-medium ${pct > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            ({pct > 0 ? '+' : ''}{pct}% here)
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <div className="text-xs text-gray-400 mb-3">{formatCurrency(rt.monthlyMaintenance)}/mo per unit</div>
                    <button
                      disabled={!canAfford}
                      onClick={() => onInstallRenewable(currentLocation, rt.id as 'solar' | 'wind', units)}
                      className={`w-full py-1.5 rounded text-xs font-medium transition-colors ${
                        canAfford ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      +{units} for {formatCurrency(totalCost)}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Battery Storage */}
          <div className="bg-gray-800 rounded-lg p-4">
            <h3 className="font-semibold text-white mb-1 flex items-center gap-2">
              <Battery size={16} className="text-cyan-400" /> Battery Storage
            </h3>
            <p className="text-xs text-gray-400 mb-3">
              Stores surplus solar/wind energy and discharges at night or when renewable output drops — reducing grid draw around the clock.
            </p>

            {!battery?.installed ? (
              <button
                disabled={money < 300000}
                onClick={() => onInstallBattery(currentLocation)}
                className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                  money >= 300000 ? 'bg-cyan-700 hover:bg-cyan-600 text-white' : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                Install Battery Storage — {formatCurrency(300000)}
                <div className="text-xs font-normal opacity-70">1 MWh · 500 kW charge/discharge rate</div>
              </button>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-gray-700 rounded p-2 text-center">
                    <div className="text-xs text-gray-400">Capacity</div>
                    <div className="font-bold text-cyan-400">1 MWh</div>
                  </div>
                  <div className="bg-gray-700 rounded p-2 text-center">
                    <div className="text-xs text-gray-400">Charge level</div>
                    <div className="font-bold text-white">{chargePercent.toFixed(0)}%</div>
                    <div className="text-xs text-gray-500">{battery.chargeLevel.toFixed(0)} kWh</div>
                  </div>
                  <div className="bg-gray-700 rounded p-2 text-center">
                    <div className="text-xs text-gray-400">Status</div>
                    <div className={`text-xs font-bold mt-0.5 ${
                      batteryAction === 'charging' ? 'text-cyan-400' :
                      batteryAction === 'discharging' ? 'text-orange-400' :
                      batteryAction === 'full' ? 'text-green-400' : 'text-gray-400'
                    }`}>
                      {batteryAction === 'charging' ? `+${Math.round(batteryFlowKW)} kW` :
                       batteryAction === 'discharging' ? `−${Math.round(batteryFlowKW)} kW` :
                       batteryAction === 'full' ? 'Full — exporting' : 'Idle'}
                    </div>
                  </div>
                </div>
                <div className="h-2 bg-gray-600 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      batteryAction === 'charging' ? 'bg-cyan-400' :
                      batteryAction === 'discharging' ? 'bg-orange-400' :
                      batteryAction === 'full' ? 'bg-green-400' : 'bg-cyan-600'
                    }`}
                    style={{ width: `${chargePercent}%` }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">Export surplus to grid</span>
                  <button
                    onClick={() => onToggleBatteryTrading(currentLocation, !battery.tradingEnabled)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      battery.tradingEnabled ? 'bg-cyan-600 text-white' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                    }`}
                  >
                    {battery.tradingEnabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  Surplus beyond battery capacity is exported at {((battery.exportFeedInFraction ?? 0.6) * 100).toFixed(0)}% of spot rate.
                  {battery.tradingEnabled ? ' Export revenue accrues hourly.' : ' Enable to earn from exports.'}
                </div>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="bg-gray-800/50 rounded-lg p-4 text-xs text-gray-400 space-y-1">
            <div className="font-semibold text-gray-300 mb-2">How it works</div>
            <div>• Solar output follows the sun: zero at night, peaks around midday. Wind is more consistent 24h.</div>
            <div>• Renewable output directly offsets how much power you draw from the grid — reducing your electricity bill each hour.</div>
            <div>• Battery absorbs surplus daytime solar and discharges at night, covering more of your 24h load.</div>
            <div>• Surplus not absorbed by the battery is exported to the grid for {((loc.batteryStorage?.exportFeedInFraction ?? 0.6) * 100).toFixed(0)}% of the spot rate.</div>
            <div>• Reach {(GREEN_CERT_RENEWABLE_FRACTION * 100).toFixed(0)}% 24h avg coverage to earn a green certification badge (+{(GREEN_CERT_REVENUE_BONUS * 100).toFixed(0)}% revenue, unlocks platinum contracts).</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GreenEnergyModal;
